/* eslint-disable no-unused-vars */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { twoDecimalWithSpaces } from '../../../helpers/formatFractional';
import dayjs from 'dayjs';
import _ from 'lodash';

import './style.scss';

export const Bubble = (props) => {
  const {
    data,
    startYear,
    endYear,
    grid = true,
    height,
    componentType,
    onBubbleDoubleClick,
    itemsType,
  } = props;

  const chartRef = useRef(null);

  useEffect(() => {
    const handleWheel = (event) => {
      const chartContainer =
        document.querySelector('.bubble-chart-container') ||
        document.querySelector('.stages-list-content');
      if (chartContainer) {
        event.preventDefault();
        const scrollSpeed = 30;
        if (event.deltaY > 0) {
          chartContainer.scrollLeft += scrollSpeed;
        } else {
          chartContainer.scrollLeft -= scrollSpeed;
        }
      }
    };

    const chartContainer =
      document.querySelector('.bubble-chart-container') ||
      document.querySelector('.stages-list-content');
    if (chartContainer) {
      chartContainer.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        chartContainer.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Обработчик двойного клика на chart
  useEffect(() => {
    if (!chartRef.current?.chart) return;

    const chart = chartRef.current.chart;
    const handleDblClick = (e) => {
      if (onBubbleDoubleClick) {
        const points = chart.series[0].points;
        let closestPoint = null;
        let minDistance = Infinity;

        points.forEach((point) => {
          const distance = Math.sqrt(
            Math.pow(e.offsetX - chart.plotLeft - point.plotX, 2) +
              Math.pow(e.offsetY - chart.plotTop - point.plotY, 2),
          );
          if (distance < minDistance && distance < 100) {
            minDistance = distance;
            closestPoint = point;
          }
        });

        if (closestPoint && closestPoint.options.itemId) {
          onBubbleDoubleClick(
            closestPoint.options.itemId,
            closestPoint.options.globalCategory,
          );
        }
      }
    };

    const container = chart.container;
    container.addEventListener('dblclick', handleDblClick);

    return () => {
      container.removeEventListener('dblclick', handleDblClick);
    };
  }, [onBubbleDoubleClick]);

  const calculateScore = (
    purpose,
    happiness,
    purposeWeights,
    happinessWeights,
    scoreMatrix,
  ) => {
    const purposeIndex = purposeWeights.findIndex(
      (weight) => purpose >= weight,
    );
    const happinessIndex = happinessWeights.findIndex(
      (weight) => happiness >= weight,
    );

    const purposeIdx =
      purposeIndex >= 0 ? purposeIndex : purposeWeights.length - 1;
    const happinessIdx =
      happinessIndex >= 0 ? happinessIndex : happinessWeights.length - 1;

    return scoreMatrix[purposeIdx][happinessIdx];
  };

  const getYValue = (item) => {
    if (
      item.global_category === 'daily_life' ||
      item.global_category === 'giving_legacy'
    ) {
      return item.necessity;
    }

    if (item.global_category === 'lifestyle_experiences') {
      const purposeWeights = [100, 50, 0];
      const happinessWeights = [100, 50, 0];
      const scoreMatrix = [
        [100, 64, 29],
        [57, 36, 14],
        [14, 7, 0],
      ];

      return calculateScore(
        item.purpose,
        item.happiness,
        purposeWeights,
        happinessWeights,
        scoreMatrix,
      );
    }

    return item.necessity || item.probability;
  };

  const chartData = (item) => {
    const itemYear = dayjs(item.start_date).year();
    const startYearForCalc =
      componentType === 'FinancialPlanStages'
        ? new Date().getFullYear()
        : startYear;

    const xPosition = (itemYear - startYearForCalc) * 150 + 100;

    const data = {
      x: xPosition,
      y: getYValue(item),
      z: item.amount,
      name: item.name,
      originalDate: item.start_date,
      itemId: item.id,
      itemImage:
        item.image || `/img/${itemsType}/${item.category.toLowerCase()}.png`,
      category: item.category,
      globalCategory: item.global_category,
      itemType: itemsType,
    };
    return data;
  };

  const remapMonthlyData = (item) => {
    const diffYear =
      dayjs(item.end_date).year() - dayjs(item.start_date).year() + 1;
    const remapData = [];
    const yearEnd = dayjs(item.start_date).endOf('year');
    for (let i = diffYear; i > 0; i--) {
      const endMonthCount = dayjs(item.end_date)
        .startOf('year')
        .diff(dayjs(item.end_date), 'month');
      if (i === 1) {
        remapData.push({
          ...item,
          start_date: item.end_date,
          amount: item.amount * (Math.abs(endMonthCount) + 1),
        });
        break;
      }
      if (i === diffYear) {
        const firstYearMonthCount =
          yearEnd.diff(dayjs(item.start_date), 'month') + 1;
        remapData.push({
          ...item,
          amount: item.amount * firstYearMonthCount,
          start_date: dayjs(yearEnd).format('YYYY-MM-DD'),
        });
      }
      if (i < diffYear && i > 1) {
        remapData.push({
          ...item,
          amount: item.amount * 12,
          start_date: yearEnd.add(i - 1, 'year').format('YYYY-MM-DD'),
        });
      }
    }

    const forChart = remapData.map((i) => chartData(i));
    return forChart;
  };

  const remapAnnualData = (item) => {
    const start = new Date(item.start_date);
    const end = new Date(item.end_date);
    const diff = dayjs(end).diff(dayjs(start), 'year');
    const remapData = [item];
    if (diff) {
      for (let i = 0; i < diff; i++) {
        remapData.push({
          ...item,
          start_date: dayjs(start).add(i + 1, 'year'),
        });
      }
    }
    const forChart = remapData.map((i) => chartData(i));
    return forChart;
  };

  const parsedData = data?.map((item) => {
    return item.frequency === 0
      ? chartData(item)
      : item.frequency === 2
        ? remapAnnualData(item)
        : remapMonthlyData(item);
  });
  const flatData = _.flatMapDeep(parsedData);

  const getBubbleRadius = (zValue) => {
    if (!flatData || flatData.length === 0) return 12.5;

    const zValues = flatData.map((d) => d.z);
    const minZ = Math.min(...zValues);
    const maxZ = Math.max(...zValues);
    const minDiameter = 25;
    const maxDiameter = 50;
    const minRadius = minDiameter / 2;
    const maxRadius = maxDiameter / 2;

    if (maxZ === minZ) return minRadius;

    const ratio = (zValue - minZ) / (maxZ - minZ);
    return minRadius + ratio * (maxRadius - minRadius);
  };

  const chartHeight = height || Math.max(600, window.innerHeight - 260);

  let yearsCount;
  if (componentType === 'FinancialPlanStages') {
    const currentYear = new Date().getFullYear();
    yearsCount = endYear - currentYear + 1;
  } else {
    yearsCount = endYear && startYear ? endYear - startYear + 1 : 10;
  }

  // Add 150px padding after the last year and 100px padding at the beginning
  const chartWidth = yearsCount * 150 + 150 + 100;

  const options = {
    credits: {
      enabled: false,
    },
    chart: {
      type: 'bubble',
      plotBorderWidth: 0,
      zooming: {
        type: 'x',
      },
      height: chartHeight,
      width: chartWidth,
      backgroundColor: 'transparent',
    },
    title: {
      text: null,
    },
    legend: {
      enabled: false,
      itemStyle: {
        fontSize: '16px',
        color: '#4D4D4D',
      },
    },
    plotOptions: {
      series: {
        stickyTracking: false,
      },
    },
    series: [
      {
        name: '',
        data: flatData,
        minSize: 25,
        maxSize: 50,
        color: {
          radialGradient: {
            cx: 0.5,
            cy: 0.5,
            r: 0.5,
          },
          stops: [
            [0, '#FB7B33'],
            [0.2, '#3A333100'],
            [1, '#3A3331B2'],
          ],
        },
        states: {
          hover: {
            brightness: 0.7,
          },
        },
      },
    ],
    xAxis: {
      type: 'linear',
      min: 0,
      max: chartWidth,
      tickInterval: 150,
      labels: { enabled: false },
      gridLineColor: 'transparent',
      lineWidth: 0,
      tickLength: 0,
    },
    yAxis: {
      gridLineColor: grid ? '#423A38' : 'transparent',
      lineWidth: grid ? 1 : 0,
      title: null,
      labels: grid
        ? {
            style: {
              fontSize: '15px',
              color: '#4D4D4D',
            },
          }
        : { enabled: false },
      tickLength: grid ? 5 : 0,
    },
    tooltip: {
      shared: true,
      backgroundColor: 'transparent',
      borderWidth: 0,
      useHTML: true,
      formatter: function () {
        const year = dayjs(this.point?.originalDate).format('YYYY');

        return `<div class="bubble-tooltip-details">
            <p class="bubble-tooltip-name">${this.point?.name}</p>
            <p class="bubble-tooltip-amount">${twoDecimalWithSpaces(this.point?.z)} SEK</p>
            <p class="bubble-tooltip-date">${year}</p>
            <img class="bubble-tooltip-image" width="115" height="auto" src="${this.point?.itemImage}" alt="${this.point?.name}" />
          </div>`;
      },
      followPointer: false,
      outside: true,
      positioner: function (labelWidth, labelHeight, point) {
        return {
          x: point.plotX + 5,
          y: point.plotY + 15,
        };
      },
      style: {
        zIndex: 9999,
      },
    },
  };

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

Bubble.propTypes = {
  data: PropTypes.array,
  startYear: PropTypes.number,
  endYear: PropTypes.number,
  grid: PropTypes.bool,
  height: PropTypes.number,
  componentType: PropTypes.string,
  onBubbleDoubleClick: PropTypes.func,
  itemsType: PropTypes.string,
};
